import * as React from 'react';
import { ComponentType } from 'react';
import { connect } from "react-redux";
import { routerActions } from 'react-router-redux';
import ReactTable, { RowInfo } from 'react-table';
import * as ReactTooltip from 'react-tooltip';
import { bindActionCreators } from "redux";
import { TrialsBreadcrumbs } from 'src/helpers/breadcrumbHelper';
import RouterHoc, { IRouterActions, IRouterProps } from 'src/hoc/RouterHoc';
import * as NavigationActions from '../../../actions/navigation';
import * as PagingActions from '../../../actions/pages/pagingPage';
import * as TrialActions from '../../../actions/trial';
import { InlineLoading } from "../../../components/common/index";
import { FormFilterInput } from '../../../components/form';
import { RestrictedLayout, TruncatedCell } from "../../../components/layouts/index";
import { Link } from "../../../components/routing/index";
import { FontAwesomeIcon, FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from '../../../dtos/Tmd.dtos';
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from '../../../helpers/authentication';
import { convertToApproximateDateString } from "../../../helpers/date";
import { findLookupValue } from "../../../helpers/lookupHelper";
import { IFormFilter, ITmdState } from "../../../reducers/index";

import './TrialsPage.scss';

interface ITrialsPageParams {}

interface ITrialsPageProps extends IRouterProps<ITrialsPageParams> {

    // From state
    formFilter: IFormFilter;
    formLength: number;
    trials: Dtos.Trial[];
    loadingTrials: boolean;
    loadTrialsSuccess: boolean;
    loadTrialsFailure: boolean;

    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    lookups: Dtos.Lookup[];

    pageSize: number;
}

interface ITrialsPageActions extends IRouterActions {
    clearTrials: TrialActions.ITrialClearActionCreator;
    loadTrials: TrialActions.ITrialLoadActionCreator;

    navigate: typeof routerActions.push;
    navigateReplace: typeof routerActions.replace;

    filter: TrialActions.ITrialFilterActionCreator;

    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;

    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;

}

type TrialsPageProps = ITrialsPageProps & ITrialsPageActions;

class TrialsPage extends React.PureComponent<TrialsPageProps, any> {

    constructor(props: TrialsPageProps) {
        super(props);

        this.clearTrials = this.clearTrials.bind(this);
    }

    componentDidMount() {
        const {
            loadTrials,
            setBreadcrumbs,
            setDrawerItems
        } = this.props;
        

        loadTrials();

        setDrawerItems([]);
        setBreadcrumbs(TrialsBreadcrumbs());
    }

    componentWillReceiveProps(_nextProps: TrialsPageProps) {
    }

    componentDidUpdate(_prevProps: TrialsPageProps, _prevState) {
    }

    componentWillUpdate(_nextProps: TrialsPageProps) {
    }

    componentWillUnmount() {
        this.clearTrials();
    }

    clearTrials() {
        this.props.clearTrials();
    }

    showTrials(): boolean {
        const {
            loadingTrials,
            loadTrialsSuccess
        } = this.props

        if (!loadingTrials && loadTrialsSuccess) {
            return true;
        }

        return false;
    }

    render() {
        const {
            loadingTrials
        } = this.props;

        return (
            <RestrictedLayout
                anyMatchpermission={Dtos.Permission.TrialView}
                loading={loadingTrials}
            >
                {this.renderContent()}
            </RestrictedLayout>
        );
    }

    renderContent() {
        const {
            permissions
        } = this.props;

        const canCreate = AuthenticationHelper.hasPermission(permissions, Dtos.Permission.TrialCreate);
        const canViewPersonnelRoles = AuthenticationHelper.hasPermission(permissions, Dtos.Permission.TrialPersonnelRoleView);

        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Trials</h1>
                    </div>
                    <div className="col d-flex justify-content-end align-items-stretch" style={{ height: '38px' }}>
                        <FormFilterInput formFilter={this.props.formFilter} onFilter={e => this.props.filter(e)} />
                        {canViewPersonnelRoles && (
                            <a
                                className="btn btn-info mr-2"
                                target="_blank"
                                href={encodeURI(
                                    `/api/personnelrole/export/-1/0/0/0?filter=${this.props.formFilter.search}&showInactive=${this.props.formFilter.showInActive}`
                                )}
                            >
                                <FontAwesomeIcon icon={FontAwesomeIcons.Solid.CLOUD_DOWNLOAD} fixedWidth />
                            </a>
                        )}

                        {canCreate && 
                            <>
                                <Link className={"btn btn-primary"} url={"/trials/create"}>Create</Link>
                            </>
                        }
                    </div>
                </div>
                {
                    !this.showTrials() ?
                        <InlineLoading absoluteCenter={true} size={50}/> :
                        null
                }
                {
                    this.showTrials() ?
                        <div className='card'>
                            <div className="patient-tables">
                                {
                                    this.createTable()
                                }
                            </div>
                        </div> :
                        null
                }
            </div>
        );
    }

    createTable() {
        const {
            trials,
            lookups,
            pageSize,
            setPageSize,
            permissions
        } = this.props

        if (!trials || trials.length == 0) {
            return (
                <div className='card card-shadow p-3'>
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon icon={FontAwesomeIcons.Solid.INFO_SQUARE} fixedWidth />
                        </div>
                        <span>There are currently no trials to view.</span>
                    </div>
                </div>
            );
        }

        const showPagination = trials.length > pageSize;

        // Filter out projects
        var trialsList = trials?.filter(t => t?.protocolID != null); 

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={trialsList}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={showPagination ? pageSize : trialsList.length}
                    pageSize={showPagination ? pageSize : trialsList.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no trials to display"
                    resizable={false}
                    onPageSizeChange={val => setPageSize(val)}
                    getTdProps={(_state, rowInfo: RowInfo, column, _instance) => {
                        return {
                            onClick: (e, handleOriginal) => {
                                const trialId = rowInfo.original.id;
                                const canView = AuthenticationHelper.hasPermission(permissions, Dtos.Permission.TrialView, trialId, undefined);
                                const canViewPersonnelRoles = AuthenticationHelper.hasPermission(permissions, Dtos.Permission.TrialPersonnelRoleView, trialId, undefined);
                                if (!canViewPersonnelRoles && column.id == "personnelCount") {
                                    return;
                                }
                                if (!canView && column.id != "personnelCount") {
                                    return;
                                }
                                const url: string = (column.id == "personnelCount") ?
                                    "/trials/" + rowInfo.original.id + "/personnel"
                                    : "/trials/" + rowInfo.original.id + "/details";
                                this.props.navigate(url);

                                if (handleOriginal) {
                                    handleOriginal()
                                }

                            }
                        }
                    }}
                    defaultSorted={[
                        {
                            id: "studyAcronym",
                            desc: false
                        }
                    ]}
                    columns={[
                        {
                            id: "id",
                            Header: ' ',
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => <FontAwesomeIcon size='lg' icon={FontAwesomeIcons.Duotone.TRIAL} fixedWidth />
                        },
                        {
                            id: "studyAcronym",
                            Header: 'Name',
                            accessor: 'studyAcronym',
                            maxWidth: 200,
                            Cell: (props) => <TruncatedCell value={props.value} />
                        },
                        {
                            id: "protocolID",
                            Header: 'Protocol ID',
                            accessor: 'protocolID',
                            maxWidth: 500,
                            Cell: (props) => <TruncatedCell value={props.value} />
                        },
                        {
                            id: "shortTitle",
                            Header: 'Short Title',
                            accessor: 'shortTitle',
                            Cell: (props) => <TruncatedCell value={props.value} />
                        },
                        {
                            id: "trialStatusId",
                            Header: 'Status',
                            accessor: 'trialStatusId',
                            maxWidth: 180,
                            Cell: (props) => <div><span data-tip={convertToApproximateDateString(props.value)}>{findLookupValue("Trial", "TrialStatusId", props.value, lookups)}</span><ReactTooltip /></div>
                        },
                        {
                            id: "personnelCount",
                            Header: 'Roles',
                            accessor: 'personnelCount',
                            maxWidth: 80,
                            Cell: (props) => <div><span className="text-info"><u>{props.value || "0"}</u></span></div>
                        },
                    ]}
                />
            </div>
        );
    }

}

const mapStateToProps = (state: ITmdState, ownProps: TrialsPageProps): ITrialsPageProps => {

    let permissions = state.user.userSummary ? state.user.userSummary.permissions : [];

    return {

        history: ownProps.history,
        location: ownProps.location,
        match: ownProps.match,

        trials: state.trials.formFilteredData!,
        formFilter: state.trials.formFilter,
        formLength: state.trials.formLength,
        lookups: state.trials.lookups,

        loadingTrials: state.trials.loadState && state.trials.loadState.status == RequestState.Pending,
        loadTrialsSuccess: state.trials.loadState && state.trials.loadState.status == RequestState.Success,
        loadTrialsFailure: state.trials.loadState && state.trials.loadState.status == RequestState.Failure,

        user: state.user.data,
        permissions: permissions,

        pageSize: state.pagingPage.pageSize
    };
};

const mapDispatchToProps = (dispatch, ownProps: TrialsPageProps): ITrialsPageActions => {
    return {
        navigate: ownProps.navigate,
        navigateReplace: ownProps.navigateReplace,

        loadTrials: bindActionCreators(TrialActions.LoadTrial, dispatch),
        clearTrials: bindActionCreators(TrialActions.Clear, dispatch),

        filter: bindActionCreators(TrialActions.Filter, dispatch),

        setPageSize: bindActionCreators(PagingActions.PageSizeChanged, dispatch),

        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch)
    };
};

export default
    RouterHoc<ITrialsPageParams, TrialsPageProps>((connect(mapStateToProps, mapDispatchToProps)(TrialsPage) as unknown as ComponentType<TrialsPageProps>)) as ComponentType<any>;
